import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectUserInformations } from "../../features/authSlice";
import { deleteCategory, selectCategoryDeleteStatus, selectCategoryDeleteError } from "../../features/categories/categoryCrudSlice";
import { hasPermission, showToast } from "../../utils/utils";
import Pagination from "../reusable/Pagination";
import Loader from "../Live/Loader";
import ActionsDropdown from "../reusable/ActionsDropdown";
import { FiEdit, FiRefreshCw } from "react-icons/fi";
import { MdDeleteOutline } from "react-icons/md";
import { FaSortUp, FaSortDown } from "react-icons/fa";
import { ReactComponent as ExcelIcon } from '../../assets/svg/Excel.svg'
import axios from "axios";
import { saveAs } from 'file-saver';
import { API } from '../../shared/api/Api';

/**
 * CategoryTable Component
 * Refactored to use navigation instead of modals
 */
const CategoryTable = ({
  categories,
  currentPage,
  totalPages,
  onSearchChange,
  onPageChange,
  loader,
  setReFetchData,
  onSort,
  currentOrderBy,
  currentOrderDir,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUserInformations);
  const deleteStatus = useSelector(selectCategoryDeleteStatus);
  const deleteError = useSelector(selectCategoryDeleteError);

  // Handle delete with confirmation
  const handleDelete = async (categoryId) => {
    if (window.confirm("Are you sure you want to delete this category? There is no going back!!")) {
      const result = await dispatch(deleteCategory({ token: user.token, categoryId }));
      
      if (deleteCategory.fulfilled.match(result)) {
        showToast('Success', 'Category deleted successfully', 'success');
        setReFetchData(true);
      } else if (deleteCategory.rejected.match(result)) {
        showToast('Error', result.payload || 'Failed to delete category', 'error');
      }
    }
  };

  // Handle export to Excel
  const handleExportToExcel = async () => {
    try {
      const { data } = await axios.get(API.Categories.app_categories_export_to_excel, {
        headers: {
          Authorization: `Bearer ${user.token}`
        },
        responseType: 'blob'
      });

      const blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, `categories_${new Date().getTime()}.xlsx`);
      showToast('Success', 'Categories exported successfully', 'success');
    } catch (error) {
      showToast('Error', 'Failed to export categories', 'error');
    }
  };

  useEffect(() => {
    if (deleteStatus === 'failed' && deleteError) {
      showToast('Error', deleteError, 'error');
    }
  }, [deleteStatus, deleteError]);

  // Column definitions with sortable property
  const columns = [
    { id: "title", label: "Category Title", sortable: true },
    { id: "description", label: "Description", sortable: true },
    { id: "created_at", label: "Created At", sortable: true },
  ];

  // Handle sorting when a column header is clicked
  const handleSortClick = (columnId) => {
    if (currentOrderBy === columnId) {
      const newDirection = currentOrderDir === "ASC" ? "DESC" : "ASC";
      onSort(columnId, newDirection);
    } else {
      onSort(columnId, "ASC");
    }
  };

  // Render sort indicator
  const renderSortIndicator = (columnId) => {
    const isActive = currentOrderBy === columnId;

    return (
      <div className="inline-flex flex-col ml-1">
        <FaSortUp
          className={`${isActive && currentOrderDir === "ASC" ? "text-primary" : "text-gray-400"} -mb-1`}
          size={12}
        />
        <FaSortDown
          className={`${isActive && currentOrderDir === "DESC" ? "text-primary" : "text-gray-400"} -mt-1`}
          size={12}
        />
      </div>
    );
  };

  const getDropdownActions = (id) => {
    const actions = [];

    if (hasPermission(user, "view_category")) {
      actions.push({
        icon: (
          <svg
            className="w-4 h-4"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
        ),
        label: "Preview",
        onClick: () => navigate(`/dashboard/categories/${id}`),
      });
    }

    if (hasPermission(user, "update_category")) {
      actions.push({
        icon: <FiEdit className="w-4 h-4" />,
        label: "Edit",
        onClick: () => navigate(`/dashboard/categories/${id}/edit`),
      });
    }

    if (hasPermission(user, "delete_category")) {
      actions.push({
        icon: <MdDeleteOutline className="w-4 h-4" />,
        label: "Delete",
        onClick: () => handleDelete(id),
      });
    }

    return actions;
  };

  return (
    <div className="w-full mx-auto mt-8 px-2 sm:px-4 lg:px-0">
      <div className="bg-white border border-gray-100 shadow-xl rounded-2xl p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="font-anek-odia text-2xl sm:text-3xl font-semibold text-gray-900">
              Categories
            </h1>
            <p className="mt-1 text-sm text-gray-500">
              Manage all course categories with sorting, search, and quick actions.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
            {/* Search */}
            <div className="relative w-full sm:w-60">
              <input
                type="text"
                placeholder="Search categories..."
                onChange={(e) => onSearchChange(e.target.value)}
                className="w-full rounded-full border border-gray-200 bg-gray-50 px-10 py-2.5 text-sm text-gray-700 placeholder:text-gray-400 outline-none focus:border-primary focus:bg-white focus:ring-1 focus:ring-primary transition"
              />
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-4.35-4.35M11 5a6 6 0 100 12 6 6 0 000-12z"
                  />
                </svg>
              </span>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setReFetchData(true)}
                className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-2 text-xs sm:text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                title="Refresh Data"
              >
                <FiRefreshCw className="w-4 h-4" />
                <span className="hidden sm:inline">Refresh</span>
              </button>

              {hasPermission(user, "export_category") && (
                <button
                  type="button"
                  onClick={handleExportToExcel}
                  className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-3 py-2 text-xs sm:text-sm font-medium text-white hover:bg-emerald-600 transition-colors"
                  title="Export to Excel"
                >
                  <ExcelIcon className="w-4 h-4" />
                  <span className="hidden sm:inline">Export</span>
                </button>
              )}

              {hasPermission(user, "create_category") && (
                <button
                  type="button"
                  onClick={() => navigate('/dashboard/categories/create')}
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white hover:bg-primary-hover transition-colors"
                  title="Add Category"
                >
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white/15">
                    <svg
                      className="w-3.5 h-3.5"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 18 18"
                    >
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 1v16M1 9h16"
                      />
                    </svg>
                  </span>
                  <span className="hidden sm:inline">New Category</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Table Container */}
        <div className="mt-6 rounded-2xl border border-gray-100 bg-white overflow-hidden">
          <div className="w-full overflow-x-auto">
            <div className="min-w-[720px]">
              {loader && (
                <div className="flex justify-center items-center py-6">
                  <Loader />
                </div>
              )}

              <table className="w-full table-auto">
                <thead className="bg-gray-50">
                  <tr>
                    {columns.map((column) => (
                      <th
                        key={column.id}
                        className={`px-4 sm:px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide ${
                          currentOrderBy === column.id
                            ? "text-primary"
                            : "text-gray-500"
                        } border-b border-gray-100 ${
                          column.sortable ? "cursor-pointer hover:bg-gray-100" : ""
                        }`}
                        onClick={
                          column.sortable
                            ? () => handleSortClick(column.id)
                            : undefined
                        }
                      >
                        <div className="flex items-center gap-1">
                          <span>{column.label}</span>
                          {column.sortable && renderSortIndicator(column.id)}
                        </div>
                      </th>
                    ))}

                    {(hasPermission(user, "update_category") ||
                      hasPermission(user, "delete_category") ||
                      hasPermission(user, "view_category")) && (
                      <th className="px-4 sm:px-6 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-100">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {categories.length === 0 && !loader ? (
                    <tr>
                      <td
                        colSpan={columns.length + 1}
                        className="px-4 sm:px-6 py-10 text-center text-gray-500"
                      >
                        No categories found
                      </td>
                    </tr>
                  ) : (
                    categories.map((category, index) => (
                      <tr
                        key={index}
                        className="group hover:bg-gray-50 transition-colors text-sm"
                      >
                        <td className="px-4 sm:px-6 py-4">
                          <div className="flex flex-col">
                            <span className="font-medium text-gray-900">
                              {category.title}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-4">
                          <p className="text-gray-600 text-sm max-w-xl truncate">
                            {category.description
                              ? category.description.length > 80
                                ? `${category.description.substring(0, 80)}...`
                                : category.description
                              : "—"}
                          </p>
                        </td>
                        <td className="px-4 sm:px-6 py-4">
                          <span className="inline-flex items-center rounded-full bg-gray-50 px-3 py-1 text-xs font-medium text-gray-600 border border-gray-100">
                            {category.created_at}
                          </span>
                        </td>
                        {(hasPermission(user, "update_category") ||
                          hasPermission(user, "delete_category") ||
                          hasPermission(user, "view_category")) && (
                          <td className="px-4 sm:px-6 py-4 text-right">
                            <ActionsDropdown actions={getDropdownActions(category.id)} />
                          </td>
                        )}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Pagination */}
        <div className="flex justify-between items-center mt-4">
          <p className="hidden sm:block text-xs text-gray-500">
            Showing page <span className="font-semibold">{currentPage}</span> of{" "}
            <span className="font-semibold">{totalPages}</span>
          </p>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        </div>
      </div>
    </div>
  );
};

export default CategoryTable;
